import { ArrowBack } from "@mui/icons-material";
import { Button, FormControlLabel, Switch, TablePagination, TextField, Typography, useTheme } from "@mui/material";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiPanelDetailDTO } from "../../types/EpiPanelDetailDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import { obtemIconeOrdem } from "../executions/Executions";
import './EpiInspectionDetail.scss';

const titles: Record<string, string> = {
	MONITORED: 'Colaboradores monitorados',
	EXAMS: 'Exames pendentes',
	TRAININGS: 'Orientações e treinamentos pendentes'
};

const pageSizes = [25, 50, 100];

type SortField = 'NAME' | 'REGISTRATION' | 'SECTOR' | 'POSITION' | 'DETAIL' | 'STATUS' | 'DUE';

const EpiInspectionDetail: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [rows, setRows] = useState<EpiPanelDetailDTO[]>([]);
	const [search, setSearch] = useState('');
	const [fieldOrder, setFieldOrder] = useState<SortField>('NAME');
	const [ordemAsc, setOrdemAsc] = useState(true);
	const [page, setPage] = useState(0);
	const [size, setSize] = useState(pageSizes[0]);
	const [loading, setLoading] = useState(true);
	const [onlyRecommended, setOnlyRecommended] = useState(true);
	const { companyId, type } = useParams();
	const navigate = useNavigate();
	const theme = useTheme();
	const { verifyPermission } = useUtils();
	const service = new EpiInspectionService();
	const showsDeadlineColumns = type !== 'MONITORED';

	function mudaOrdem(field: SortField) {
		setOrdemAsc(field === fieldOrder ? !ordemAsc : true);
		setFieldOrder(field);
		setPage(0);
	}

	useEffect(() => {
		const loadRows = async () => {
			const response = await service.getPanelDetail(Number(companyId), type || '');
			setRows(response?.data || []);
			setLoading(false);
		};
		loadRows();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EPI_INSPECTION');
		}
	}, []);

	const sorted = sort(filter());
	const currentPage = Math.min(page, Math.max(0, Math.ceil(sorted.length / size) - 1));
	const visible = sorted.slice(currentPage * size, currentPage * size + size);

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-inspection-detail">
				<div className="top">
					<Button
						variant="contained"
						startIcon={<ArrowBack />}
						onClick={() => navigate('/inspecao-epis')}
					>
						Voltar ao painel
					</Button>
					<Typography variant="h6">{titles[type || ''] || 'Detalhamento'}</Typography>
					<TextField
						label="Buscar colaborador"
						id="group-search"
						size="small"
						value={search}
						onChange={(e) => { setSearch(e.target.value); setPage(0); }}
					/>
					{type === 'EXAMS' && (
						<FormControlLabel
							control={
								<Switch
									checked={onlyRecommended}
									onChange={(e) => { setOnlyRecommended(e.target.checked); setPage(0); }}
								/>
							}
							label="Visualizar somente recomendados"
						/>
					)}
				</div>
				{loading
					? getLoading()
					: !sorted.length
						? <div className="sem-execucoes">
							<Typography variant="h6">Nenhum registro encontrado.</Typography>
						</div>
						: <Fragment>
							<div className={showsDeadlineColumns ? 'tabela com-prazo' : 'tabela sem-prazo'}>
								<div className="header-tabela">
									<p onClick={() => mudaOrdem('NAME')}>
										Colaborador {obtemIconeOrdem('NAME', fieldOrder, ordemAsc)}
									</p>
									<p onClick={() => mudaOrdem('REGISTRATION')}>
										Matrícula {obtemIconeOrdem('REGISTRATION', fieldOrder, ordemAsc)}
									</p>
									<p onClick={() => mudaOrdem('SECTOR')}>
										Setor {obtemIconeOrdem('SECTOR', fieldOrder, ordemAsc)}
									</p>
									<p onClick={() => mudaOrdem('POSITION')}>
										Cargo {obtemIconeOrdem('POSITION', fieldOrder, ordemAsc)}
									</p>
									<p onClick={() => mudaOrdem('DETAIL')}>
										Detalhe {obtemIconeOrdem('DETAIL', fieldOrder, ordemAsc)}
									</p>
									{showsDeadlineColumns && (
										<Fragment>
											<p onClick={() => mudaOrdem('STATUS')}>
												Situação {obtemIconeOrdem('STATUS', fieldOrder, ordemAsc)}
											</p>
											<p onClick={() => mudaOrdem('DUE')}>
												Prazo {obtemIconeOrdem('DUE', fieldOrder, ordemAsc)}
											</p>
										</Fragment>
									)}
								</div>
								<div className="body-table">
									{visible.map((row, index) => (
										<div className="linha-empresa" key={`${row.employeeId}-${index}`}>
											<p>{row.employeeName || '—'}</p>
											<p>{row.registration || '—'}</p>
											<p>{row.sector || '—'}</p>
											<p>{row.position || '—'}</p>
											<p>{row.detail || '—'}</p>
											{showsDeadlineColumns && (
												<Fragment>
													<p>{row.status || '—'}</p>
													<p style={{ color: deadlineColor(row.dueDate) }}>
														{row.dueDate || '—'}
													</p>
												</Fragment>
											)}
										</div>
									))}
								</div>
							</div>
							<div className="paginacao">
								<TablePagination
									component="div"
									rowsPerPageOptions={pageSizes}
									count={sorted.length}
									rowsPerPage={size}
									page={currentPage}
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
									labelRowsPerPage="Registros por página"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
								/>
							</div>
						</Fragment>
				}
			</div>
		</Fragment>
	);

	function deadlineColor(dueDate: string) {
		const time = parseBrDate(dueDate);
		if (time === null) {
			return undefined;
		}
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return time < today.getTime() ? theme.palette.error.main : theme.palette.success.main;
	}

	function filter() {
		const filteredByRecommended = type === 'EXAMS' && onlyRecommended
			? rows.filter(item => item.recommended === true)
			: rows;
		if (!search) {
			return filteredByRecommended;
		}
		return filteredByRecommended.filter(item => (item.employeeName || '').toUpperCase().includes(search.toUpperCase())
			|| (item.registration || '').includes(search));
	}

	function sort(filtered: EpiPanelDetailDTO[]) {
		return [...filtered].sort((first, second) => {
			if (fieldOrder === 'DUE') {
				const firstTime = parseBrDate(first.dueDate);
				const secondTime = parseBrDate(second.dueDate);
				if (firstTime === null || secondTime === null) {
					return firstTime === secondTime ? 0 : firstTime === null ? 1 : -1;
				}
				return ordemAsc ? firstTime - secondTime : secondTime - firstTime;
			}
			const firstValue = sortValue(first);
			const secondValue = sortValue(second);
			return ordemAsc
				? firstValue.localeCompare(secondValue)
				: secondValue.localeCompare(firstValue);
		});
	}

	function sortValue(row: EpiPanelDetailDTO) {
		switch (fieldOrder) {
			case 'REGISTRATION':
				return row.registration || '';
			case 'SECTOR':
				return row.sector || '';
			case 'POSITION':
				return row.position || '';
			case 'DETAIL':
				return row.detail || '';
			case 'STATUS':
				return row.status || '';
			default:
				return row.employeeName || '';
		}
	}
};

export function parseBrDate(date: string) {
	if (!date) {
		return null;
	}
	const [day, month, year] = date.split('/');
	if (!day || !month || !year) {
		return null;
	}
	return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
}

export default EpiInspectionDetail;
